import useSWR from 'swr';

import SmoothLink from '~/modules/elements/smooth-link';
import { fetchBoard, LEADERBOARD_URL } from '~/modules/leaderboard/client';
import LeaderboardRow from '~/modules/leaderboard/leaderboard-row';
import ScoreboardPanel from '~/modules/scoreboard/scoreboard-panel';
import { songSelectionPath } from '~/routes/route-paths';

/**
 * The global board, read straight from the cached `GET /leaderboard` projection. Deliberately
 * skipped by `useKeyboardNav`: making 50 rows keyboard-traversable would add a navigation sink that
 * TV users hit by accident, so on a TV this is a display of the top 10 and nothing more.
 */
function LeaderboardPanel({ className, listClassName }: { className?: string; listClassName?: string }) {
  const { data, error, isLoading } = useSWR(LEADERBOARD_URL, fetchBoard, {
    revalidateOnFocus: false,
  });

  return (
    // Same box the main menu sits in, so the panel reads as part of it rather than a bolted-on
    // widget — and the same `ScoreboardPanel` the post-game boards use, so a board looks like a board
    <ScoreboardPanel
      className={`p-4 sm:p-6 ${className ?? ''}`}
      // Passed through so a caller can trade the shared five-row list height for one of its own —
      // the tiled menu's rail is as tall as the screen and would otherwise stop a long way short.
      listClassName={listClassName}
      title="Global leaderboard"
      subtitle="Highest scores from the last 14 days"
      isLoading={isLoading}
      error={error}
      isEmpty={data?.entries.length === 0}
      emptyMessage="No results yet"
      data-test="leaderboard-panel">
      {data?.entries.map((entry, index) => (
        // Each row opens the song list on its song. The row's own fill is translucent, so the
        // link's hover fill shows through it
        <SmoothLink
          key={`${entry.songId}-${entry.name}-${index}`}
          to={songSelectionPath(entry.songId)}
          className="block rounded-xl text-inherit no-underline transition-colors hover:bg-white/10"
          data-test="leaderboard-song-link">
          <LeaderboardRow entry={entry} position={index + 1} />
        </SmoothLink>
      ))}
    </ScoreboardPanel>
  );
}

export default LeaderboardPanel;
